import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { PROFILE_PAGE_PENDING_APPROVAL_VARIANT } from '../../util/urlHelpers';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { isUserAuthorized } from '../../util/userHelpers';
import { transitions } from '../../transactions/transactionDefaultProcess';
import { bidAlias, LISTING_STATE_CLOSED, LISTING_STATE_DRAFT } from '../../util/types';
import { inviteProfessionals, updateListingData } from '../../util/api';


const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/ProfilePage/SET_INITIAL_STATE';

export const SHOW_USER_REQUEST = 'app/ProfilePage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/ProfilePage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/ProfilePage/SHOW_USER_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/ProfilePage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/ProfilePage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/ProfilePage/QUERY_LISTINGS_ERROR';

export const QUERY_REVIEWS_REQUEST = 'app/ProfilePage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS = 'app/ProfilePage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/ProfilePage/QUERY_REVIEWS_ERROR';

export const CONTACT_PROFESSIONAL_REQUEST = 'app/ProfilePage/CONTACT_PROFESSIONAL_REQUEST';
export const CONTACT_PROFESSIONAL_SUCCESS = 'app/ProfilePage/CONTACT_PROFESSIONAL_SUCCESS';
export const CONTACT_PROFESSIONAL_ERROR = 'app/ProfilePage/CONTACT_PROFESSIONAL_ERROR';

export const INVITE_PROFESSIONAL_REQUEST = 'app/ProfilePage/INVITE_PROFESSIONAL_REQUEST';
export const INVITE_PROFESSIONAL_SUCCESS = 'app/ProfilePage/INVITE_PROFESSIONAL_SUCCESS';
export const INVITE_PROFESSIONAL_ERROR = 'app/ProfilePage/INVITE_PROFESSIONAL_ERROR';

export const SEND_PROPOSAL_REQUEST = 'app/ProfilePage/SEND_PROPOSAL_REQUEST';
export const SEND_PROPOSAL_SUCCESS = 'app/ProfilePage/SEND_PROPOSAL_SUCCESS';
export const SEND_PROPOSAL_ERROR = 'app/ProfilePage/SEND_PROPOSAL_ERROR';

export const QUERY_CURRENT_USER_LISTINGS_REQUEST = 'app/ProfilePage/QUERY_CURRENT_USER_LISTINGS_REQUEST';
export const QUERY_CURRENT_USER_LISTINGS_SUCCESS = 'app/ProfilePage/QUERY_CURRENT_USER_LISTINGS_SUCCESS';
export const QUERY_CURRENT_USER_LISTINGS_ERROR = 'app/ProfilePage/QUERY_CURRENT_USER_LISTINGS_ERROR';
// ================ Reducer ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
  inviteProfessionalInProgress: false,
  inviteProfessionalError: null,
  contactProfessionalInProgress: false,
  contactProfessionalError: null,
  sendProposalInProgress: false,
  sendProposalError: null,
  currentUserListingRefs: [],
  queryCurrentUserListingsInProgress: false,
  queryCurrentUserListingsError: null,
};

export default function profilePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };
    case SHOW_USER_REQUEST:
      return { ...state, userShowError: null, userId: payload.userId };
    case SHOW_USER_SUCCESS:
      return state;
    case SHOW_USER_ERROR:
      return { ...state, userShowError: payload };

    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,

        // Empty listings only when user id changes
        userListingRefs: payload.userId === state.userId ? state.userListingRefs : [],

        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return { ...state, userListingRefs: payload.listingRefs };
    case QUERY_LISTINGS_ERROR:
      return { ...state, userListingRefs: [], queryListingsError: payload };
    case QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsError: null };
    case QUERY_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case QUERY_REVIEWS_ERROR:
      return { ...state, reviews: [], queryReviewsError: payload };
    case CONTACT_PROFESSIONAL_REQUEST:
      return {
        ...state,
        contactProfessionalInProgress: true,
        contactProfessionalError: null,
      };
    case CONTACT_PROFESSIONAL_SUCCESS:
      return {
        ...state,
        contactProfessionalInProgress: false,
        contactProfessionalError: null,
      };
    case CONTACT_PROFESSIONAL_ERROR:
      return {
        ...state,
        contactProfessionalInProgress: false,
        contactProfessionalError: payload,
      };
    case INVITE_PROFESSIONAL_REQUEST:
      return {
        ...state,
        inviteProfessionalInProgress: true,
        inviteProfessionalError: null,
      };
    case INVITE_PROFESSIONAL_SUCCESS:
      return {
        ...state,
        inviteProfessionalInProgress: false,
        inviteProfessionalError: null,
      };
    case INVITE_PROFESSIONAL_ERROR:
      return {
        ...state,
        inviteProfessionalInProgress: false,
        inviteProfessionalError: payload,
      };
    case SEND_PROPOSAL_REQUEST:
      return {
        ...state,
        sendProposalInProgress: true,
      };
    case SEND_PROPOSAL_SUCCESS:
      return {
        ...state,
        sendProposalInProgress: false,
      };
    case SEND_PROPOSAL_ERROR:
      return {
        ...state,
        sendProposalInProgress: false,
        sendProposalError: payload,
      };
    case QUERY_CURRENT_USER_LISTINGS_REQUEST:
      return {
        ...state,
        queryCurrentUserListingsInProgress: true,
      };
    case QUERY_CURRENT_USER_LISTINGS_SUCCESS:
      return {
        ...state,
        queryCurrentUserListingsInProgress: false,
        currentUserListingRefs: payload.listingRefs,
      };
    case QUERY_CURRENT_USER_LISTINGS_ERROR:
      return {
        ...state,
        queryCurrentUserListingsInProgress: false,
        queryCurrentUserListingsError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showUserRequest = userId => ({
  type: SHOW_USER_REQUEST,
  payload: { userId },
});

export const showUserSuccess = () => ({
  type: SHOW_USER_SUCCESS,
});

export const showUserError = e => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = userId => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: { userId },
});

export const queryListingsSuccess = listingRefs => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { listingRefs },
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = reviews => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryReviewsError = e => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

export const contactProfessionalRequest = () => ({
  type: CONTACT_PROFESSIONAL_REQUEST,
});
export const contactProfessionalSuccess = () => ({
  type: CONTACT_PROFESSIONAL_SUCCESS,
});
export const contactProfessionalError = e => ({
  type: CONTACT_PROFESSIONAL_ERROR,
  error: true,
  payload: e,
});

export const inviteProfessionalRequest = () => ({
  type: INVITE_PROFESSIONAL_REQUEST,
});
export const inviteProfessionalSuccess = () => ({
  type: INVITE_PROFESSIONAL_SUCCESS,
});
export const inviteProfessionalError = e => ({
  type: INVITE_PROFESSIONAL_ERROR,
  payload: e,
});
export const queryCurrentUserListingsRequest = () => ({
  type: QUERY_CURRENT_USER_LISTINGS_REQUEST,
});
export const queryCurrentUserListingsSuccess = listingRefs => ({
  type: QUERY_CURRENT_USER_LISTINGS_SUCCESS,
  payload: { listingRefs },
});
export const queryCurrentUserListingsError = e => ({
  type: QUERY_CURRENT_USER_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const sendProposalRequest = () => ({ type: SEND_PROPOSAL_REQUEST });
export const sendProposalSuccess = () => ({ type: SEND_PROPOSAL_SUCCESS });
export const sendProposalError = e => ({
  type: SEND_PROPOSAL_ERROR,
  error: true,
  payload: e,
});
// ================ Thunks ================ //

export const queryUserListings = (userId, config) => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(userId));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .query({
      author_id: userId,
      include: ['author', 'images'],
      'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    })
    .then(response => {
      // Pick only the id and type properties from the response listings
      const listings = response.data.data;
      const listingRefs = listings
        .filter(l => l => !l.attributes.deleted && l.attributes.state === 'published')
        .map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(listingRefs));
      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

export const queryUserReviews = userId => (dispatch, getState, sdk) => {
  sdk.reviews
    .query({
      subject_id: userId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(queryReviewsSuccess(reviews));
    })
    .catch(e => dispatch(queryReviewsError(e)));
};

export const showUser = (userId, config) => (dispatch, getState, sdk) => {
  dispatch(showUserRequest(userId));
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const userFields = config?.user?.userFields;
      const sanitizeConfig = { userFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(showUserSuccess());
      return response;
    })
    .catch(e => dispatch(showUserError(storableError(e))));
};

export const queryCurrentUserListings = () => (dispatch, getState, sdk) => {
  dispatch(queryCurrentUserListingsRequest());
  setTimeout(() => {
    const currentUser = getState().user.currentUser;
    if (currentUser) {
      return sdk.ownListings
        .query({
          states: "published",
          include: ['images'],
          include: ['author', 'images'],
          'fields.image': [
            // Listing
            'variants.landscape',
            'variants.landscape-2x',
            'variants.landscape-crop',
            'variants.landscape-crop2x',
            'variants.listing-card',
            // Custom variants
            'variants.profile-crop',

            // Image carousel
            'variants.scaled-small',
            'variants.scaled-medium',
            'variants.scaled-large',
            'variants.scaled-xlarge',
          ],
          'imageVariant.profile-page': 'w:380;h:380;fit:crop',
        })
        .then(response => {
          const listings = response.data.data;
          const listingRefs = listings.map(({ id, type }) => ({
            id,
            type,
          }));
          dispatch(addMarketplaceEntities(response));
          dispatch(queryCurrentUserListingsSuccess(listingRefs));
        })

        .catch(e => dispatch(queryCurrentUserListingsError(storableError(e))));
    }
  }, [500]);
};
export const inviteProfessional = params => (dispatch, getState, sdk) => {
  const { profileId, projectTitle, projectId, listingId, config, publicData, title,existingInvites } = params || {};
  dispatch(inviteProfessionalRequest());
  return inviteProfessionals(params)
    .then(response => {
      const message = `Invitation for ${projectTitle}`
      dispatch(contactProfessional(listingId, message, projectTitle, projectId, publicData, title,existingInvites,config));
      dispatch(inviteProfessionalSuccess(response));
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => dispatch(inviteProfessionalError(storableError(e))));
};

export const contactProfessional = (listingId, message, projectTitle, projectId, publicData, title,existingInvites,config) => (dispatch, getState, sdk) => {
  dispatch(contactProfessionalRequest());
  const bodyParams = {
    transition: transitions.TRANSITION_PRO_DISCUSSION,
    processAlias: bidAlias,
    params: {
      listingId,
      protectedData: {
        projectTitle,
        projectId,
        publicData,
        title
      },
    },
  };

  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      const updatedInvites = Array.isArray(existingInvites)
        ? [...existingInvites, transactionId.uuid]
        : [transactionId.uuid];

      updateListingData({
        listingId: projectId,
        publicData: {
          existingInvites: updatedInvites,
        },
      });
      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(contactProfessionalSuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(contactProfessionalError(storableError(e)));
      throw e;
    });
};

export const sendProposal = params => (dispatch, getState, sdk) => {
  dispatch(sendProposalRequest());

  const { listingId, originalListingId, originalListingTitle, budget } = params;

  const bodyParams = {
    transition: transitions.TRANSITION_PROPOSAL,
    processAlias: bidAlias,
    params: {
      listingId,
      protectedData: {
        quote: {
          price: {
            amount: budget.amount,
            currency: budget.currency,
          },
        },
        originalListingId,
        originalListingTitle,
      },
    },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      dispatch(sendProposalSuccess());
      dispatch(fetchCurrentUserHasOrdersSuccess(true));
      return transactionId;
    })
    .catch(e => {
      dispatch(sendProposalError(storableError(e)));
      throw e;
    });
};

const isCurrentUser = (userId, cu) => userId?.uuid === cu?.id?.uuid;

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const userId = new UUID(params.id);
  const isPreviewForCurrentUser = params.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;
  const currentUser = getState()?.user?.currentUser;
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  if (isPreviewForCurrentUser) {
    return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(() => {
      if (isCurrentUser(userId, currentUser) && isUserAuthorized(currentUser)) {
        // Scenario: 'active' user somehow tries to open a link for "variant" profile
        return Promise.all([
          dispatch(showUser(userId, config)),
          // dispatch(queryCurrentUserListings()),
          dispatch(queryUserListings(userId, config)),
          dispatch(queryUserReviews(userId)),
        ]);
      } else if (isCurrentUser(userId, currentUser)) {
        // Handle a scenario, where user (in pending-approval state)
        // tries to see their own profile page.
        // => just set userId to state
        return dispatch(showUserRequest(userId));
      } else {
        return Promise.resolve({});
      }
    });
  }

  // Fetch data for plain profile page.
  // Note 1: returns 404s if user is not 'active'.
  // Note 2: In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  if (!canFetchData) {
    return Promise.resolve();
  }

  return Promise.all([
    dispatch(fetchCurrentUser(fetchCurrentUserOptions)),
    dispatch(showUser(userId, config)),
    dispatch(queryCurrentUserListings()),
    dispatch(queryUserListings(userId, config)),
    dispatch(queryUserReviews(userId)),
  ]);
};
